/**
 * Plays a chunked HTTP stream of raw PCM through the Web Audio graph, starting on the first chunk.
 *
 * **Why not `new Audio(url)`.** A media element wants a *file it can seek*; we serve a *stream*.
 * Measured against a server emitting exactly what `/api/voice/speak` writes, iOS `AVPlayer` fails
 * the response outright (`-11850` / `-12939`, "the server is not correctly configured") because it
 * byte-range-seeks and our response is chunked with no `Content-Length`. **Safari and every iOS
 * browser sit on that same AVFoundation stack**, so `<audio>` is not a safe bet here either —
 * Chromium's ffmpeg pipeline accepts the stream, and that is precisely the trap: it works on the
 * developer's machine and fails for a large share of real users.
 *
 * Feeding the graph directly sidesteps all of it. There is no codec and no container to negotiate:
 * we own the format end to end (24 kHz, 16-bit signed, mono), so the samples go straight to the
 * output.
 */

import { MIN_GAP_SECONDS, SILENCE_RMS, type SpeechGap } from './speech-script';
import { applyPreferredSink } from './voice-audio-prefs';

/** The format `/api/voice/speak` emits, matching the WAV header the route writes. */
export const PCM_SAMPLE_RATE = 24_000;
/** The route writes a 44-byte canonical RIFF/WAVE prelude before the samples. */
export const PCM_HEADER_BYTES = 44;

export interface PcmStreamHandlers {
  /** The moment sound exists — first buffer scheduled on a running context, not first byte. */
  onPlaying: () => void;
  /** The stream ended and the last scheduled buffer finished, or it failed. Always exactly once. */
  onFinished: () => void;
}

/**
 * Little-endian signed 16-bit → the float samples the graph takes.
 *
 * Exported so a test can pin the conversion (sign, endianness, full-scale) without a network or a
 * speaker — getting this subtly wrong produces audio that plays and sounds like noise.
 */
// The buffer parameter is explicit because `copyToChannel` will not take a `Float32Array` backed by
// a possibly-shared buffer, which is what the bare type widens to.
export function int16ToFloat32(bytes: Uint8Array): Float32Array<ArrayBuffer> {
  const frames = Math.floor(bytes.length / 2);
  const out = new Float32Array(new ArrayBuffer(frames * 4));
  for (let i = 0; i < frames; i++) {
    const sample = (bytes[i * 2] | (bytes[i * 2 + 1] << 8)) << 16 >> 16; // sign-extend
    out[i] = sample / 32_768;
  }
  return out;
}

export class PcmStreamPlayer {
  private context: AudioContext | null = null;
  private controller: AbortController | null = null;
  /** Where the next chunk is scheduled, in context time — keeps playback gapless, and once the
   *  stream ends it is also exactly when the last sample is heard. */
  private nextStartAt = 0;
  /**
   * Context time spent NOT playing our samples, because the queue ran dry and the next chunk had to be
   * scheduled at "now" instead of where the last one ended.
   *
   * The read-along marker lives on the PCM timeline — the gaps it aligns to are measured in samples from
   * the first one — so the clock it reads has to be the same timeline. `currentTime` is a wall clock: it
   * keeps advancing through a network stall, and counting that silence as audio heard desynced the marker
   * permanently the first time the stream starved. iOS never had this because `playerTime` counts samples
   * the node actually rendered, which is why its timing was right and web's drifted.
   */
  private silentDrift = 0;
  /**
   * The scheduled nodes, held only so the garbage collector cannot take them mid-playback.
   *
   * WebKit measurably does: without this the audio still starts, and then the tail is collected
   * along with any completion event it owed us.
   */
  private sources = new Set<AudioBufferSourceNode>();
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  private startedPlaying = false;
  private done = false;
  /** Context time of the first scheduled sample — everything the caller asks about is relative to it. */
  private startedAt: number | null = null;
  private ended = false;
  private suspended = false;
  /**
   * The silences found in the audio so far — what lets the read-along marker follow the VOICE rather than
   * a guess about it. Two renders of the same brief differed by 7% in length and by 6 seconds in total
   * pause time, so where the pauses actually are is the only reliable answer.
   */
  private gaps: SpeechGap[] = [];
  private analysedSamples = 0;
  private windowSum = 0;
  private windowSamples = 0;
  private openGapStart: number | null = null;
  /** A chunk boundary can land mid-sample; half a sample played as a whole one is audible. */
  private carry = new Uint8Array(0);
  private headerRemaining = PCM_HEADER_BYTES;

  async start(url: string, handlers: PcmStreamHandlers, outputDeviceId: string | null = null): Promise<void> {
    const AudioContextCtor =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      handlers.onFinished();
      return;
    }
    // Constructed inside the click handler, so autoplay policy lets it run.
    this.context = new AudioContextCtor();
    this.controller = new AbortController();

    try {
      // A new context can still be born `suspended` — WebKit measurably does, even from inside a
      // gesture — and buffers then schedule happily against a clock that is not running. Never
      // AWAIT this: the promise was measured never settling in a context with no output, which
      // would wedge the whole feature. Fire it and let the completion poll below deal in the
      // audio clock's own reality.
      void this.context.resume?.().catch(() => {});
      // Route to the SAME speaker the voice call uses — a chosen device (a Jabra) is honoured, and
      // an unremembered one prefers the real 'default' output. Not awaited: setSinkId reroutes the
      // whole context output even mid-playback, so the network wait covers it and a slow resolve
      // never delays first sound. See applyPreferredSink.
      void applyPreferredSink(this.context, outputDeviceId);
      const response = await fetch(url, {
        credentials: 'include', // the route authenticates by cookie
        signal: this.controller.signal,
      });
      if (!response.ok || !response.body) {
        this.finish(handlers);
        return;
      }
      const reader = response.body.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) this.consume(value, handlers);
      }
      this.ended = true;
      this.scheduleCompletion(handlers);
    } catch {
      // An abort is our own stop() and has already settled; anything else degrades to idle. The
      // brief is still on screen to read, so this is a degraded surface, not a broken page.
      if (!this.done) this.finish(handlers);
    }
  }

  /**
   * How much of the brief has been HEARD, from the audio clock.
   *
   * The audio clock is the only one that answers this: it does not advance while the context is
   * suspended, while a slow network starves the queue, or before the first buffer plays. A wall clock
   * started at the click would drift away from the voice in every one of those cases, and the marker it
   * drives would drift with it.
   */
  get elapsedSeconds(): number {
    const context = this.context;
    if (!context || this.startedAt === null) return 0;
    const heard = context.currentTime - this.startedAt - this.silentDrift;
    return Math.min(Math.max(heard, 0), this.bufferedSeconds);
  }

  /** Audio actually received. Once `streamEnded` is true this is the whole brief's length. */
  get bufferedSeconds(): number {
    // Minus the silence, for the same reason: this is a length of AUDIO, not a span of wall clock.
    return this.startedAt === null ? 0 : this.nextStartAt - this.startedAt - this.silentDrift;
  }

  /** The response is fully read — nothing further will arrive, so `bufferedSeconds` is now the truth. */
  get streamEnded(): boolean {
    return this.ended;
  }

  /** Every silence measured so far, in seconds from the first sample. See `alignSegments`. */
  get silences(): SpeechGap[] {
    return this.gaps;
  }

  get paused(): boolean {
    return this.suspended;
  }

  /**
   * Hold the voice where it is, without giving up the stream.
   *
   * Suspending the context stops its clock, which is what makes this safe: chunks that arrive while
   * paused are still scheduled against `nextStartAt`, which is ahead of a clock that is not moving, so
   * they queue rather than being dropped as "in the past". Download continues; only playback waits.
   */
  pause(): void {
    if (this.done || this.suspended || !this.context) return;
    this.suspended = true;
    void this.context.suspend?.().catch(() => {});
  }

  resume(): void {
    if (this.done || !this.suspended || !this.context) return;
    this.suspended = false;
    // Not awaited, for the same reason `start` does not await the first resume: the promise was
    // measured never settling in a context with no output.
    void this.context.resume?.().catch(() => {});
  }

  stop(): void {
    this.done = true;
    if (this.endTimer) clearTimeout(this.endTimer);
    this.endTimer = null;
    this.controller?.abort();
    this.sources.clear();
    void this.context?.close();
    this.context = null;
  }

  /**
   * Finish once the AUDIO clock has actually passed the last scheduled sample.
   *
   * Polling the context's own `currentTime` rather than either alternative, both of which were
   * measured failing: an `onended` listener depends on the engine still holding a node it has no
   * further use for (WebKit never delivered it, leaving the button on "Stop" over silence), and a
   * single wall-clock `setTimeout` computed up front is wrong by however long the context stays
   * suspended. `currentTime` advances only while audio really plays, so this is right in both cases
   * — it waits out a slow start instead of declaring the brief over during it.
   */
  private scheduleCompletion(handlers: PcmStreamHandlers): void {
    const poll = () => {
      const context = this.context;
      if (!context || this.done) return;
      if (context.currentTime >= this.nextStartAt) {
        this.finish(handlers);
        return;
      }
      const remainingMs = (this.nextStartAt - context.currentTime) * 1000;
      this.endTimer = setTimeout(poll, Math.min(Math.max(remainingMs, 50), 500));
    };
    poll();
  }

  private consume(chunk: Uint8Array, handlers: PcmStreamHandlers): void {
    const context = this.context;
    if (!context || this.done) return;

    let incoming = chunk;
    if (this.headerRemaining > 0) {
      const drop = Math.min(this.headerRemaining, incoming.length);
      incoming = incoming.subarray(drop);
      this.headerRemaining -= drop;
      if (incoming.length === 0) return;
    }

    const merged = new Uint8Array(this.carry.length + incoming.length);
    merged.set(this.carry, 0);
    merged.set(incoming, this.carry.length);
    const usable = (merged.length >> 1) << 1;
    this.carry = merged.subarray(usable);
    if (usable === 0) return;

    const samples = int16ToFloat32(merged.subarray(0, usable));
    this.findSilences(samples);
    const buffer = context.createBuffer(1, samples.length, PCM_SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    // Never schedule in the past: a late chunk would otherwise be dropped rather than queued.
    const scheduleAt = Math.max(this.nextStartAt, context.currentTime);
    if (this.startedAt !== null) this.silentDrift += scheduleAt - this.nextStartAt; // starved: silence
    this.nextStartAt = scheduleAt;
    if (this.startedAt === null) this.startedAt = this.nextStartAt;
    source.start(this.nextStartAt);
    this.nextStartAt += buffer.duration;
    this.sources.add(source);
    source.onended = () => this.sources.delete(source);

    if (!this.startedPlaying) {
      this.startedPlaying = true;
      handlers.onPlaying();
    }
  }

  /**
   * Walk the samples in 20ms windows and record the quiet stretches.
   *
   * Cheap by construction — one multiply-add per sample, on samples already being converted one by one —
   * and stateful ACROSS chunks, because a pause lands mid-chunk about as often as not. The lead-in shows
   * up as a gap starting at zero, which is exactly how `alignSegments` finds where the voice truly begins.
   */
  private findSilences(samples: Float32Array): void {
    const windowLength = PCM_SAMPLE_RATE / 50; // 20ms
    for (let i = 0; i < samples.length; i++) {
      this.windowSum += samples[i] * samples[i];
      this.windowSamples++;
      if (this.windowSamples < windowLength) continue;

      const rms = Math.sqrt(this.windowSum / this.windowSamples);
      const start = this.analysedSamples / PCM_SAMPLE_RATE;
      this.analysedSamples += this.windowSamples;
      this.windowSum = 0;
      this.windowSamples = 0;

      if (rms < SILENCE_RMS) {
        if (this.openGapStart === null) this.openGapStart = start;
        continue;
      }
      if (this.openGapStart === null) continue;
      // Closed by sound: keep it only if it was long enough to be a pause rather than a stop consonant.
      if (start - this.openGapStart >= MIN_GAP_SECONDS) this.gaps.push({ start: this.openGapStart, end: start });
      this.openGapStart = null;
    }
  }

  private finish(handlers: PcmStreamHandlers): void {
    if (this.done) return;
    this.done = true;
    if (this.endTimer) clearTimeout(this.endTimer);
    this.endTimer = null;
    this.sources.clear();
    void this.context?.close();
    this.context = null;
    handlers.onFinished();
  }
}
